import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog, DialogContent, DialogProps, DialogTitle, Typography, Grid } from '@mui/material'

import { useGetScreenshotCommentQuery } from '../../api/core'
import { useCurrentUserLanguage } from '../../features/account/hooks/useCurrentUserLanguage'
import { useContentTranslation } from '../../hooks/translationHooks'
import { useAnalystComment } from '../../hooks/useAnalystComment'
import GRCircularProgress from '../GRCircularProgress/GRCircularProgress'
import { ReviewContent, ReviewHeader } from '../Review/Review'
import { TranslationToggle } from '../TranslationToggle/TranslationToggle'

/**
 * Component for loading and presenting analyst comment based on comment identifier
 */

type AnalystCommentDialogProps = DialogProps & {
  commentId?: string | null
  hasAccessRights?: boolean
  gameName?: string
  isScreenshotAnalysisComment?: boolean
}

export const AnalystCommentDialog: FC<AnalystCommentDialogProps> = ({
  commentId,
  hasAccessRights = false,
  gameName,
  isScreenshotAnalysisComment,
  ...dialogProps
}) => {
  const { t } = useTranslation()
  const language = useCurrentUserLanguage()
  const { analystComment, isLoading } = useAnalystComment(commentId, isScreenshotAnalysisComment || !commentId)
  const { data: screenshotComment } = useGetScreenshotCommentQuery(commentId as string, { skip: !isScreenshotAnalysisComment || !commentId })
  const { isTranslating, translatedContent, translationError, handleTranslateClick, resetTranslation, displayContent } = useContentTranslation()

  const commentContent = isScreenshotAnalysisComment
    ? screenshotComment?.screenshotComment?.content?.comment?.[language] || screenshotComment?.screenshotComment?.content?.comment?.['en'] || ''
    : analystComment?.analysisComment?.content?.comment?.[language] || analystComment?.analysisComment?.content?.comment?.['en'] || ''

  const showTranslationToggle = commentContent && language !== 'en' && hasAccessRights

  useEffect(() => {
    if (!dialogProps.open || !commentId) {
      resetTranslation()
    }
  }, [dialogProps.open, commentId, resetTranslation])

  return (
    <Dialog sx={{ zIndex: 1301 }} maxWidth="md" fullWidth disableRestoreFocus keepMounted {...dialogProps}>
      {isLoading ? (
        <DialogContent>
          <GRCircularProgress />
        </DialogContent>
      ) : (
        <>
          <DialogTitle>
            <ReviewHeader
              analyst={isScreenshotAnalysisComment ? screenshotComment?.analyst : analystComment?.analyst}
              title={t('reviews:label_analyst_comment')}
              subtitle={gameName}
              direction="row-reverse"
              alignItems="center"
              titleProps={{ color: 'primary' }}
            />
            {showTranslationToggle && (
              <Grid container mt={2}>
                <Grid item>
                  <TranslationToggle
                    isTranslating={isTranslating}
                    hasTranslation={!!translatedContent}
                    error={translationError}
                    onToggle={() => handleTranslateClick(commentContent, language)}
                  />
                </Grid>
              </Grid>
            )}
          </DialogTitle>
          <DialogContent dividers>
            {hasAccessRights ? (
              <ReviewContent contentHtml={displayContent(commentContent)} />
            ) : (
              <Typography variant="body1">{t('reviews:label_not_for_free')}</Typography>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}
